'use client';

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { LogOut, Edit3, Eye, Save, Pause, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function AdminToolbar() {
  const { isAdmin, setIsAdmin, language, setLanguage, isFrozen, setIsFrozen } = useLanguage();
  const [savingStatus, setSavingStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    const handleSaving = (e: any) => {
      setSavingStatus(e.detail.status);
      if (e.detail.status === 'saved' || e.detail.status === 'error') {
        setTimeout(() => setSavingStatus('idle'), 2000);
      }
    };

    window.addEventListener('contentSaving', handleSaving as any);
    return () => window.removeEventListener('contentSaving', handleSaving as any);
  }, []);

  if (!isAdmin) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-4 px-6 py-3 bg-background/80 backdrop-blur-xl border border-primary/20 rounded-2xl shadow-2xl shadow-primary/20 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center gap-2 pr-4 border-r border-border">
        <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
        <span className="text-xs font-bold tracking-wider uppercase text-foreground/70">
          Admin Mode
        </span>
      </div>

      <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-xl border border-border">
        <button
          onClick={() => setLanguage('en')}
          className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${language === 'en'
              ? 'bg-primary text-white shadow-lg shadow-primary/30'
              : 'text-muted-foreground hover:text-foreground'
            }`}
        >
          EN
        </button>
        <button
          onClick={() => setLanguage('ar')}
          className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${language === 'ar'
              ? 'bg-primary text-white shadow-lg shadow-primary/30'
              : 'text-muted-foreground hover:text-foreground'
            }`}
        >
          AR
        </button>
      </div>

      <button
        onClick={() => setIsFrozen(!isFrozen)}
        className={cn(
          "flex items-center gap-2 px-4 py-1.5 text-xs font-bold rounded-lg transition-all shadow-lg",
          isFrozen
            ? "bg-amber-500 text-white shadow-amber-500/20"
            : "bg-muted text-foreground hover:bg-muted/80"
        )}
      >
        {isFrozen ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
        {isFrozen ? "Unfreeze" : "Freeze"}
      </button>

      {savingStatus !== 'idle' && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 border border-border animate-in fade-in zoom-in duration-300">
          {savingStatus === 'saving' && <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
          {savingStatus === 'saved' && <div className="text-emerald-500 font-bold text-[10px]">✓ SAVED</div>}
          {savingStatus === 'error' && <div className="text-red-500 font-bold text-[10px]">× ERROR</div>}
          {savingStatus === 'saving' && <span className="text-[10px] font-bold text-muted-foreground uppercase">Saving...</span>}
        </div>
      )}

      <button
        onClick={() => {
          setIsAdmin(false);
          try {
            localStorage.removeItem('isAdmin');
          } catch (e) {
            // storage unavailable (e.g. iOS "Block All Cookies") - safe to ignore
          }
          // Clear session cookie
          document.cookie = "admin_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        }}
        className="flex items-center gap-2 px-4 py-1.5 text-xs font-bold text-white bg-destructive hover:bg-destructive/90 rounded-lg transition-all shadow-lg shadow-destructive/20"
      >
        <LogOut className="w-3.5 h-3.5" />
        Exit
      </button>
    </div>
  );
}
